import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Log } from 'ng2-logger';
import { TranslateService, TranslateParser } from '@ngx-translate/core';

import { TableConfig } from '@app/shared/util/table-config.utils';
import { ConfirmationModalComponent } from '@app/shared/components';
import { TicketService } from '@app/core/services/ticket.service';
import { Ticket } from '@app/shared/models/ticket.model';

import { ToolHistoryService } from '@services/tool-history.service';
import { ToolLifecycleHistory } from '@models/tool-lifecycle-history.model';

import * as FileSaver from 'file-saver';
import * as _ from "lodash";

import { TicketDetailsModalComponent } from '../../../ticket-management/components';
import { ToastsManager } from "@app/shared/util/toastr.service";

@Component({
    selector: 'app-lifecycle-history',
    templateUrl: './lifecycle-history.component.html',
    styleUrls: ['./lifecycle-history.component.scss']
})
export class LifecycleHistoryComponent implements OnInit {

    _logger = Log.create(LifecycleHistoryComponent.name);

    @ViewChild(TicketDetailsModalComponent, {static: true}) ticketDetailsModal: TicketDetailsModalComponent;
    @ViewChild(ConfirmationModalComponent, {static: true}) confirmationModal: ConfirmationModalComponent;

    toolLifecycleHistory: ToolLifecycleHistory[] = [];
    tableConfig = new TableConfig();

    tableFilters = [];
    reportFilters = [];

    isLoadingToolLifecycleHistory = false;
    isLoadingDownload: boolean;

    selectedTicket: Ticket;

    constructor(private toolHistoryService: ToolHistoryService,
                private toastr: ToastsManager,
                private ticketService: TicketService,
                private translationService: TranslateService,
                private translateParser: TranslateParser) {
    }

    ngOnInit() {
        this.tableConfig.sorter = {
            field: 'date',
            IsSortingAscending: false
        };
        this.getToolLifecycleHistory(this.tableConfig);
    }

    onSelectTicket(ticketId: number) {
        this.getTicketById(ticketId);
    }

    onCancelTicket(ticket) {
        this._logger.info("confirmCloseTicket:: Open 'close confirmation popup' for ticket:", this.selectedTicket);
        this.selectedTicket = ticket;
        this.ticketDetailsModal.close();
        this.confirmationModal.open();
    }

    confirmCancelTicket(event) {
        this._logger.info("onCloseTicket:: Scan badge successfully: ", event);
        this.confirmationModal.close();
        this._logger.info("onCloseTicket:: Closed confirmation modal");
        this.ticketService.closeTicket(this.selectedTicket, event.boschId).subscribe(
            (data: any) => {
                this._logger.info("onCloseTicket:: Ticket was deleted", this.selectedTicket);
                this.getToolLifecycleHistory();
                this.toastr.warning(this.buildCloseMessage(this.selectedTicket.id));
                this.ticketDetailsModal.close();
            },
            (error: any) => {
                this._logger.info("onCloseTicket:: API returned error", error);
            }
        );
    }

    onFilterChange(tableFilters: any = [], reportFilters: any = []) {
        this.tableFilters = tableFilters;
        this.reportFilters = reportFilters;

        this.tableConfig.filters = this.reportFilters.concat(this.tableFilters);

        this.getToolLifecycleHistory(this.tableConfig);
    }

    onSortChange(tableConfig: TableConfig) {
        this.getToolLifecycleHistory(tableConfig);
    }

    getToolLifecycleHistory(tableConfig = this.tableConfig) {
        this.isLoadingToolLifecycleHistory = true;
        this.toolHistoryService.getLifecycleHistory(tableConfig).subscribe(data => {
                this.toolLifecycleHistory = data.toolLifecycleHistory;
                this.tableConfig.pager.count = data.count;
                this.isLoadingToolLifecycleHistory = false;
            },
            (error: any) => {
                this.isLoadingToolLifecycleHistory = false;
            });
    }

    onLifecycleHistoryDownload(tableConfig = this.tableConfig) {
        this.isLoadingDownload = true;
        this.toolHistoryService.downloadLifecycleHistoryReport(tableConfig).subscribe(
            (data: HttpResponse<any>) => {
                if (data.body != null) {
                    const blob = new Blob([data.body], {type: 'application/xsls'});
                    const filename = data.headers.get('x-filename');
                    FileSaver.saveAs(blob, filename);
                }
                this.isLoadingDownload = false;
            },
            (error: any) => {
                this.isLoadingDownload = false;
                this.toastr.error(this.translationService.instant('Server_Error'));
            });
    }

    onPageChange(tableConfig: TableConfig) {
        this.tableFilters = tableConfig.filters;
        tableConfig.filters = _.union(this.reportFilters, this.tableFilters);
        this.getToolLifecycleHistory(tableConfig);
    }

    onDownloadFile(fileToDownload: any) {
        this.ticketService.downloadFile(fileToDownload)
    }

    private getTicketById(ticketId: number): void {
        this.isLoadingToolLifecycleHistory = true
        this.ticketService.getTicketById(ticketId).subscribe(
            (data: Ticket) => {
                this._logger.info('getTicketById:: API returned data ', data);
                this.selectedTicket = data;
                this.ticketDetailsModal.open(this.selectedTicket);
                this.isLoadingToolLifecycleHistory = false;
            },
            (error: any) => {
                this._logger.info('getTicketById:: API returned an error', error);
            }
        )
    }

    private buildCloseMessage(ticketId: number) {
        const message = this.translationService.instant('admin.tickets.toasts.closed');
        return this.translateParser.interpolate(message, {ticketId: ticketId});
    }
}
